/* eslint @typescript-eslint/strict-boolean-expressions: off */

import React from "react";
import cn from "classnames";
import debounce from "lodash/debounce";
import produce from "immer";
import sortBy from "lodash/sortBy";
import styles from "./visuals-options.module.css";
import { AxisOptions, VisualTableColumn } from "src/gql/visuals/types";
import { Box } from "src/components/box/box";
import { ButtonGroup } from "src/components/ui-components/ButtonOld/ButtonGroup";
import { ColumnMapping } from "src/gql/visuals/types";
import { FieldLabel } from "src/components/input/fields";
import { Fields } from "src/components/input/fields";
import { Icon } from "src/components/icon/icon";
import { InputCheckbox, InputText } from "src/components/input/input";
import { InputSelect } from "src/components/input/input";
import { QueryResult } from "src/utils/results/types";
import { QueryResultColumn } from "src/utils/results/types";
import { SeriesOptions } from "src/gql/visuals/types";
import { SeriesOptionsRow } from "src/gql/visuals/types";
import { Size } from "src/components/theme/theme";
import { ValuesOptionsRow } from "src/gql/visuals/types";
import { VisualChart } from "src/gql/visuals/types";
import { VisualCounter } from "src/gql/visuals/types";
import { VisualTable } from "src/gql/visuals/types";
import { WidgetsEntriesButton } from "src/components/widgets/entries";
import { isVisualChart } from "src/gql/visuals/visuals";
import { isVisualCounter } from "src/gql/visuals/visuals";
import { isVisualTable } from "src/gql/visuals/visuals";
import { parseIntBounded } from "src/utils/numbers/numbers";
import { parseIntFallback } from "src/utils/numbers/numbers";
import { preventDefault } from "src/utils/events/events";
import { resultPrimitive } from "src/utils/results/values";
import { defaultTickFormats } from "src/components/chart/format";
import {
  useEditorLastVisualUpdatingState,
  useVisualSaveAndDispatch,
} from "./context";
import { useEditorQueryTemp } from "./context";
import { useEditorResult } from "./context";
import { useEditorVisual } from "./context";
import { visualSeriesIcon } from "src/gql/visuals/visuals";
import { Grid } from "src/components/grid/grid";
import { VisualUpdatingState } from "./state";
import { HexColorPicker, HexColorInput } from "react-colorful";
import Popover, { positionDefault } from "@reach/popover";
import { fromPairs, pick } from "lodash";

export interface ChartColumns {
  columns: QueryResultColumn[]; // All result columns.
  x?: QueryResultColumn; // The column mapped to x.
  ys: QueryResultColumn[]; // The columns mapped to y.
  z?: QueryResultColumn; // The column mapped to z.
  group?: QueryResultColumn; // The column used for group by.
}

export const EditorVisualsOptions: React.FC = () => {
  const visual = useEditorVisual();
  const result = useEditorResult();

  if (!visual || !result?.matrix?.rows.length) {
    return null;
  } else if (isVisualTable(visual)) {
    return <OptionsTable visual={visual} result={result} />;
  } else if (isVisualCounter(visual)) {
    return <OptionsCounter visual={visual} result={result} />;
  } else if (isVisualChart(visual)) {
    return <OptionsChart visual={visual} result={result} />;
  } else {
    return null;
  }
};

const OptionsTable: React.FC<{
  visual: VisualTable;
  result: QueryResult;
}> = (props) => {
  const saveAndDispatch = useVisualSaveAndDispatch();

  const onName = (event: React.ChangeEvent<HTMLInputElement>) => {
    saveAndDispatch({ ...props.visual, name: event.target.value });
  };

  const onDescription = (event: React.ChangeEvent<HTMLInputElement>) => {
    saveAndDispatch({ ...props.visual, description: event.target.value });
  };

  return (
    <form onSubmit={preventDefault}>
      <Options className={styles.table}>
        <OptionsHeader />
        <OptionsBox title="Table options">
          <OptionsFields size="xs">
            <FieldLabel label="Title" size="xs">
              <InputText
                placeholder="24h volume"
                value={props.visual.name ?? ""}
                onChange={onName}
                size="sm"
                border="none"
              />
            </FieldLabel>
            <FieldLabel label="Description" size="xs">
              <InputText
                placeholder={props.visual.query_details?.description ?? ""}
                value={props.visual.description ?? ""}
                onChange={onDescription}
                size="sm"
                border="none"
              />
            </FieldLabel>
          </OptionsFields>
        </OptionsBox>
        <hr />
        {props.result.matrix?.columns.map((c, i) => (
          <OptionsBox key={i} title={`Column ${i}: ${c.name}`}>
            <OptionsTableColumn {...props} column={c} />
          </OptionsBox>
        ))}
      </Options>
    </form>
  );
};

interface OptionUpdate {
  option: keyof VisualTableColumn;
  value: VisualTableColumn[keyof VisualTableColumn];
}

const OptionsTableColumn: React.FC<{
  visual: VisualTable;
  column: QueryResultColumn;
}> = (props) => {
  const {
    column: { name, type },
  } = props;
  const saveAndDispatch = useVisualSaveAndDispatch();
  const columns = props.visual.options?.columns ?? [];
  const columnOptions = columns.find((column) => column.name === name);

  const onChange = (optionUpdates: Array<OptionUpdate>) => {
    let { visual: updatedVisual } = props;

    const updateVisual = <O extends keyof VisualTableColumn>({
      option,
      value,
    }: {
      option: O;
      value: VisualTableColumn[O];
    }) => {
      updatedVisual = produce(updatedVisual, (draft) => {
        draft.options = draft.options ?? {};
        draft.options.columns = draft.options.columns ?? [];
        const columnOptionsIndex = columns.findIndex(
          ({ name }) => name === columnOptions?.name
        );

        if (columnOptionsIndex >= 0) {
          draft.options.columns[columnOptionsIndex][option] = value;
        } else {
          draft.options.columns.push({
            name,
            [option]: value,
          });
        }
      });
    };

    optionUpdates.forEach(updateVisual);

    saveAndDispatch(updatedVisual);
  };

  return (
    <OptionsFields size="xs">
      <FieldLabel label="Title" size="xs">
        <InputText
          placeholder="Price"
          value={columnOptions?.title ?? ""}
          onChange={(event) =>
            onChange([{ option: "title", value: event.target.value }])
          }
          size="sm"
          border="none"
        />
      </FieldLabel>
      <FieldLabel label="Align" size="xs">
        <InputSelect
          value={columnOptions?.alignContent ?? ""}
          onChange={(event) =>
            onChange([
              {
                option: "alignContent",
                value: event.target.value,
              },
            ])
          }
          size="sm"
          border="none"
        >
          <option value="left">Left</option>
          <option value="center">Center</option>
          <option value="right">Right</option>
        </InputSelect>
      </FieldLabel>
      <FieldLabel label="Format" size="xs">
        <InputText
          placeholder="0.0"
          value={columnOptions?.numberFormat ?? ""}
          onChange={(event) =>
            onChange([{ option: "numberFormat", value: event.target.value }])
          }
          size="sm"
          border="none"
        />
      </FieldLabel>
      {type === "float" && (
        <FieldLabel label="Type" size="xs">
          <InputSelect
            value={columnOptions?.type ?? "normal"}
            onChange={(event) => {
              onChange([
                {
                  option: "coloredPositiveValues",
                  value: false,
                },
                {
                  option: "coloredNegativeValues",
                  value: false,
                },
                {
                  option: "type",
                  value: event.target.value,
                },
              ]);
            }}
            size="sm"
            border="none"
          >
            <option value="normal">Normal</option>
            <option value="progressbar">Progress bar</option>
          </InputSelect>
        </FieldLabel>
      )}
      <InputCheckbox
        checked={!!columnOptions?.isHidden}
        onChange={(event) =>
          onChange([{ option: "isHidden", value: event.target.checked }])
        }
        size="sm"
        border="none"
      >
        Hide column
      </InputCheckbox>
      {type === "float" && columnOptions?.type !== "progressbar" && (
        <>
          <InputCheckbox
            checked={!!columnOptions?.coloredPositiveValues}
            onChange={(event) =>
              onChange([
                {
                  option: "coloredPositiveValues",
                  value: event.target.checked,
                },
              ])
            }
            size="sm"
            border="none"
          >
            Colored positive values
          </InputCheckbox>
          <InputCheckbox
            checked={!!columnOptions?.coloredNegativeValues}
            onChange={(event) =>
              onChange([
                {
                  option: "coloredNegativeValues",
                  value: event.target.checked,
                },
              ])
            }
            size="sm"
            border="none"
          >
            Colored negative values
          </InputCheckbox>
        </>
      )}
      {type === "float" && columnOptions?.type === "progressbar" && (
        <InputCheckbox
          checked={
            !!columnOptions?.coloredNegativeValues ||
            !!columnOptions?.coloredPositiveValues
          }
          onChange={(event) => {
            onChange([
              {
                option: "coloredPositiveValues",
                value: event.target.checked,
              },
              {
                option: "coloredNegativeValues",
                value: event.target.checked,
              },
            ]);
          }}
          size="sm"
          border="none"
        >
          Colored positive/negative values
        </InputCheckbox>
      )}
    </OptionsFields>
  );
};

const OptionsCounter: React.FC<{
  visual: VisualCounter;
  result: QueryResult;
}> = (props) => {
  const saveAndDispatch = useVisualSaveAndDispatch();
  const fields = props.result.matrix?.columns.map((c) => c.name) ?? [];

  const onName = (event: React.ChangeEvent<HTMLInputElement>) => {
    saveAndDispatch({ ...props.visual, name: event.target.value });
  };

  const onDescription = (event: React.ChangeEvent<HTMLInputElement>) => {
    saveAndDispatch({ ...props.visual, description: event.target.value });
  };

  const onDecimals = (event: React.ChangeEvent<HTMLInputElement>) => {
    saveAndDispatch(
      produce(props.visual, (draft) => {
        draft.options.stringDecimal = parseIntBounded(event.target.value, 0, 9);
      })
    );
  };

  const onString =
    (key: "stringPrefix" | "stringSuffix" | "counterLabel") =>
    (event: React.ChangeEvent<HTMLInputElement>) => {
      saveAndDispatch(
        produce(props.visual, (draft) => {
          draft.options[key] = event.target.value;
        })
      );
    };

  const onData = (event: React.ChangeEvent<HTMLSelectElement>) => {
    saveAndDispatch(
      produce(props.visual, (draft) => {
        draft.options.counterColName = event.target.value;
      })
    );
  };

  const onRowNumber = (event: React.ChangeEvent<HTMLInputElement>) => {
    saveAndDispatch(
      produce(props.visual, (draft) => {
        draft.options.rowNumber = parseIntFallback(event.target.value, 1);
      })
    );
  };

  const onColoredPositiveValues = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    saveAndDispatch(
      produce(props.visual, (draft) => {
        draft.options = draft.options ?? {};
        draft.options.coloredPositiveValues = event.target.checked;
      })
    );
  };

  const onColoredNegativeValues = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    saveAndDispatch(
      produce(props.visual, (draft) => {
        draft.options = draft.options ?? {};
        draft.options.coloredNegativeValues = event.target.checked;
      })
    );
  };

  return (
    <form onSubmit={preventDefault}>
      <Options className={styles.counter}>
        <OptionsHeader />
        <OptionsBox title="Counter options">
          <OptionsFields size="xs">
            <FieldLabel label="Title" size="xs">
              <InputText
                placeholder="24h volume"
                value={props.visual.name ?? ""}
                onChange={onName}
                size="sm"
                border="none"
              />
            </FieldLabel>
            <FieldLabel label="Description" size="xs">
              <InputText
                placeholder={props.visual.query_details?.description ?? ""}
                value={props.visual.description ?? ""}
                onChange={onDescription}
                size="sm"
                border="none"
              />
            </FieldLabel>
            <OptionsFieldData
              label="Column"
              values={["", ...fields]}
              value={props.visual.options.counterColName ?? ""}
              onChange={onData}
            />
            <FieldLabel label="Row number" size="xs">
              <InputText
                min={1}
                placeholder="1"
                value={props.visual.options.rowNumber ?? ""}
                onChange={onRowNumber}
                size="sm"
                border="none"
              />
            </FieldLabel>
            <InputCheckbox
              checked={Boolean(props.visual.options.coloredPositiveValues)}
              onChange={onColoredPositiveValues}
              size="sm"
              border="none"
            >
              Colored positive values
            </InputCheckbox>
            <InputCheckbox
              checked={Boolean(props.visual.options.coloredNegativeValues)}
              onChange={onColoredNegativeValues}
              size="sm"
              border="none"
            >
              Colored negative values
            </InputCheckbox>
          </OptionsFields>
        </OptionsBox>
        <OptionsBox title="Formatting">
          <OptionsFields size="xs">
            <FieldLabel label="Prefix" size="xs">
              <InputText
                placeholder="$"
                value={props.visual.options.stringPrefix ?? ""}
                onChange={onString("stringPrefix")}
                size="sm"
                border="none"
              />
            </FieldLabel>
            <FieldLabel label="Suffix" size="xs">
              <InputText
                placeholder="M"
                value={props.visual.options.stringSuffix ?? ""}
                onChange={onString("stringSuffix")}
                size="sm"
                border="none"
              />
            </FieldLabel>
            <FieldLabel label="Label" size="xs">
              <InputText
                placeholder="Current price"
                value={props.visual.options.counterLabel ?? ""}
                onChange={onString("counterLabel")}
                size="sm"
                border="none"
              />
            </FieldLabel>
            <FieldLabel label="Decimals" size="xs">
              <InputText
                placeholder="0"
                value={props.visual.options.stringDecimal ?? "0"}
                onChange={onDecimals}
                size="sm"
                border="none"
              />
            </FieldLabel>
          </OptionsFields>
        </OptionsBox>
      </Options>
    </form>
  );
};

const OptionsChart: React.FC<{
  visual: VisualChart;
  result: QueryResult;
}> = (props) => {
  const saveAndDispatch = useVisualSaveAndDispatch();
  const columns = createChartColumns(props.visual, props.result);
  const isStacking = props.visual.options.series?.stacking === "stack";
  const isShowTotal = props.visual.options.series?.showTotal === true;
  const isScatter = props.visual.options.globalSeriesType === "scatter";
  const isLine = props.visual.options.globalSeriesType === "line";
  const isPie = props.visual.options.globalSeriesType === "pie";

  const onName = (event: React.ChangeEvent<HTMLInputElement>) => {
    saveAndDispatch({ ...props.visual, name: event.target.value });
  };

  const onDescription = (event: React.ChangeEvent<HTMLInputElement>) => {
    saveAndDispatch({ ...props.visual, description: event.target.value });
  };

  const onLegend = (event: React.ChangeEvent<HTMLInputElement>) => {
    saveAndDispatch(
      produce(props.visual, (draft) => {
        draft.options.legend = { enabled: event.target.checked };
      })
    );
  };

  const onStacking = (event: React.ChangeEvent<HTMLInputElement>) => {
    saveAndDispatch(
      produce(props.visual, (draft) => {
        draft.options.series = draft.options.series || {};
        draft.options.series.stacking = event.target.checked ? "stack" : null;
      })
    );
  };

  const onShowTotal = (event: React.ChangeEvent<HTMLInputElement>) => {
    saveAndDispatch(
      produce(props.visual, (draft) => {
        draft.options.series = draft.options.series || {};
        draft.options.series.showTotal = event.target.checked;
      })
    );
  };

  const onPercent = (event: React.ChangeEvent<HTMLInputElement>) => {
    saveAndDispatch(
      produce(props.visual, (draft) => {
        draft.options.series = draft.options.series || {};
        draft.options.series.percentValues = event.target.checked;
      })
    );
  };

  const onLabels = (event: React.ChangeEvent<HTMLInputElement>) => {
    saveAndDispatch(
      produce(props.visual, (draft) => {
        draft.options.showDataLabels = event.target.checked;
      })
    );
  };

  return (
    <form onSubmit={preventDefault}>
      <Options className={styles.chart}>
        <OptionsHeader />
        <OptionsBox title="Chart options">
          <OptionsFields size="xs">
            <FieldLabel label="Title" size="xs">
              <InputText
                placeholder="My chart"
                value={props.visual.name ?? ""}
                onChange={onName}
                size="sm"
                border="none"
              />
            </FieldLabel>
            <FieldLabel label="Description" size="xs">
              <InputText
                placeholder={props.visual.query_details?.description ?? ""}
                value={props.visual.description ?? ""}
                onChange={onDescription}
                size="sm"
                border="none"
              />
            </FieldLabel>
            <InputCheckbox
              checked={Boolean(props.visual.options.legend?.enabled)}
              onChange={onLegend}
              size="sm"
              border="none"
            >
              Show chart legend
            </InputCheckbox>
            <InputCheckbox
              checked={isStacking}
              onChange={onStacking}
              disabled={isScatter || isLine || isPie}
              size="sm"
              border="none"
            >
              Enable stacking
            </InputCheckbox>
            <InputCheckbox
              checked={isShowTotal}
              onChange={onShowTotal}
              disabled={!isStacking}
              size="sm"
              border="none"
            >
              Show Total in tooltip
            </InputCheckbox>
            <InputCheckbox
              checked={Boolean(props.visual.options.series?.percentValues)}
              onChange={onPercent}
              disabled={isPie}
              size="sm"
              border="none"
            >
              Normalize to percentage
            </InputCheckbox>
            <InputCheckbox
              checked={Boolean(props.visual.options.showDataLabels)}
              onChange={onLabels}
              disabled={isStacking}
              size="sm"
              border="none"
            >
              Show data labels
            </InputCheckbox>
          </OptionsFields>
        </OptionsBox>
        <OptionsChartColumns visual={props.visual} columns={columns} />
        <OptionsChartAxis visual={props.visual} />
        <hr />
        <OptionsChartSeries
          visual={props.visual}
          result={props.result}
          columns={columns}
        />
        {props.visual.options.globalSeriesType === "pie" && (
          <OptionsChartValues
            visual={props.visual}
            result={props.result}
            columns={columns}
          />
        )}
      </Options>
    </form>
  );
};

const OptionsChartAxis: React.FC<{
  visual: VisualChart;
}> = (props) => {
  const saveAndDispatch = useVisualSaveAndDispatch();
  const xAxis = props.visual.options.xAxis;
  const yAxis0 = props.visual.options.yAxis?.[0];
  const yAxis1 = props.visual.options.yAxis?.[1];

  const setSortX = (event: React.ChangeEvent<HTMLInputElement>) => {
    saveAndDispatch(
      produce(props.visual, (draft) => {
        draft.options.sortX = event.target.checked;
      })
    );
  };

  const setReverseX = (event: React.ChangeEvent<HTMLInputElement>) => {
    saveAndDispatch(
      produce(props.visual, (draft) => {
        draft.options.reverseX = event.target.checked;
      })
    );
  };

  const replaceX = (axis: AxisOptions) => {
    saveAndDispatch(
      produce(props.visual, (draft) => {
        draft.options.xAxis = axis;
      })
    );
  };

  const replaceY = (axis: AxisOptions, index: 0 | 1) => {
    saveAndDispatch(
      produce(props.visual, (draft) => {
        draft.options.yAxis = draft.options.yAxis || [];
        draft.options.yAxis[index] = axis;
      })
    );
  };

  const setTitle = (
    axis: AxisOptions,
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    return produce(axis, (draft) => {
      draft.title
        ? (draft.title.text = event.target.value)
        : (draft.title = { text: event.target.value });
    });
  };

  const setLog = (
    axis: AxisOptions,
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    return produce(axis, (draft) => {
      draft.type = event.target.checked ? "logarithmic" : "-";
    });
  };

  const setTickFormat = (
    axis: AxisOptions,
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    return produce(axis, (draft) => {
      draft.tickFormat = event.target.value;
    });
  };

  const toggleYAxis1 = () => {
    saveAndDispatch(
      produce(props.visual, (draft) => {
        if (yAxis1) {
          draft.options.yAxis = [yAxis0];
          draft.options.numberFormatRightYAxisSeries = undefined;
        } else {
          draft.options.yAxis = [yAxis0, { type: "linear" }];
        }
      })
    );
  };

  const onLabelsFormat = (event: React.ChangeEvent<HTMLInputElement>) => {
    saveAndDispatch(
      produce(props.visual, (draft) => {
        draft.options.numberFormat = event.target.value;
      })
    );
  };

  const onLabelsFormatRightYAxisSeries = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    saveAndDispatch(
      produce(props.visual, (draft) => {
        draft.options.numberFormatRightYAxisSeries = event.target.value;
      })
    );
  };

  if (!xAxis) {
    throw new Error("Missing required x-axis.");
  }

  if (!yAxis0) {
    throw new Error("Missing required left y-axis.");
  }

  if (props.visual.options.globalSeriesType === "pie") {
    return (
      <OptionsBox title="Pie options">
        <OptionsFields size="xs">
          <FieldLabel label="Label format" size="xs">
            <InputText
              placeholder="0.0"
              value={props.visual.options.numberFormat ?? ""}
              onChange={onLabelsFormat}
              size="sm"
              border="none"
            />
          </FieldLabel>
        </OptionsFields>
      </OptionsBox>
    );
  }

  return (
    <div>
      <OptionsBox title="X-axis options">
        <OptionsFields size="xs">
          <FieldLabel label="Axis title" size="xs">
            <InputText
              placeholder="Date"
              value={xAxis?.title?.text ?? ""}
              onChange={(event) => replaceX(setTitle(xAxis, event))}
              size="sm"
              border="none"
            />
          </FieldLabel>
          <InputCheckbox
            checked={Boolean(props.visual.options.sortX)}
            onChange={(event) => setSortX(event)}
            size="sm"
            border="none"
          >
            Sort values
          </InputCheckbox>
          <InputCheckbox
            checked={Boolean(props.visual.options.reverseX)}
            onChange={(event) => setReverseX(event)}
            size="sm"
            border="none"
          >
            Reverse values
          </InputCheckbox>
          <InputCheckbox
            checked={Boolean(props.visual.options.xAxis.type === "logarithmic")}
            onChange={(event) => replaceX(setLog(xAxis, event))}
            size="sm"
            border="none"
          >
            Logarithmic
          </InputCheckbox>
          <FieldLabel label="Tick format" size="xs">
            <InputText
              placeholder={
                props.visual.options.series?.percentValues
                  ? defaultTickFormats.percentage
                  : defaultTickFormats.number
              }
              value={xAxis.tickFormat ?? ""}
              onChange={(event) => replaceX(setTickFormat(xAxis, event))}
              size="sm"
              border="none"
            />
          </FieldLabel>
        </OptionsFields>
      </OptionsBox>
      <OptionsBox title={yAxis1 ? "Left y-axis options" : "Y-axis options"}>
        <OptionsFields size="xs">
          <FieldLabel label="Axis title" size="xs">
            <InputText
              placeholder="Price"
              value={yAxis0?.title?.text ?? ""}
              onChange={(event) => replaceY(setTitle(yAxis0, event), 0)}
              size="sm"
              border="none"
            />
          </FieldLabel>
          <InputCheckbox
            checked={Boolean(yAxis0.type === "logarithmic")}
            onChange={(event) => replaceY(setLog(yAxis0, event), 0)}
            size="sm"
            border="none"
          >
            Logarithmic
          </InputCheckbox>
          <FieldLabel label="Tick format" size="xs">
            <InputText
              placeholder={
                props.visual.options.series?.percentValues
                  ? defaultTickFormats.percentage
                  : defaultTickFormats.number
              }
              value={yAxis0.tickFormat ?? ""}
              onChange={(event) => replaceY(setTickFormat(yAxis0, event), 0)}
              size="sm"
              border="none"
            />
          </FieldLabel>
          <FieldLabel label="Label format" size="xs">
            <InputText
              placeholder="0.0"
              value={props.visual.options.numberFormat ?? ""}
              onChange={onLabelsFormat}
              size="sm"
              border="none"
            />
          </FieldLabel>
          <InputCheckbox
            checked={Boolean(yAxis1)}
            onChange={toggleYAxis1}
            size="sm"
            border="none"
          >
            Enable right y-axis
          </InputCheckbox>
        </OptionsFields>
      </OptionsBox>
      {yAxis1 && (
        <OptionsBox title="Right y-axis options">
          <OptionsFields size="xs">
            <FieldLabel label="Axis title" size="xs">
              <InputText
                placeholder="Price"
                value={yAxis1?.title?.text ?? ""}
                onChange={(event) => replaceY(setTitle(yAxis1, event), 1)}
                size="sm"
                border="none"
              />
            </FieldLabel>
            <InputCheckbox
              checked={Boolean(yAxis1.type === "logarithmic")}
              onChange={(event) => replaceY(setLog(yAxis0, event), 1)}
              size="sm"
              border="none"
            >
              Logarithmic
            </InputCheckbox>
            <FieldLabel label="Tick format" size="xs">
              <InputText
                placeholder={
                  props.visual.options.series?.percentValues
                    ? defaultTickFormats.percentage
                    : defaultTickFormats.number
                }
                value={yAxis1.tickFormat ?? ""}
                onChange={(event) => replaceY(setTickFormat(yAxis1, event), 1)}
                size="sm"
                border="none"
              />
            </FieldLabel>
            <FieldLabel label="Label format" size="xs">
              <InputText
                placeholder={props.visual.options.numberFormat || "0.0"}
                value={props.visual.options.numberFormatRightYAxisSeries ?? ""}
                onChange={onLabelsFormatRightYAxisSeries}
                size="sm"
                border="none"
              />
            </FieldLabel>
          </OptionsFields>
        </OptionsBox>
      )}
    </div>
  );
};

const OptionsChartColumns: React.FC<{
  visual: VisualChart;
  columns: ChartColumns;
}> = (props) => {
  const saveAndDispatch = useVisualSaveAndDispatch();
  const mapping = props.visual.options.columnMapping ?? {};
  const columns = props.columns.columns.map((c) => c.name);
  const numberColumns = props.columns.columns
    .filter(({ type }) => type === "float")
    .map((c) => c.name);
  const group = props.columns.group?.name;
  const x = props.columns.x?.name;
  const z = props.columns.z?.name;
  const values = ["", ...columns];
  const numberValues = ["", ...numberColumns];

  const replaceMapping = (mapping: ColumnMapping) => {
    saveAndDispatch(
      produce(props.visual, (draft) => {
        draft.options.columnMapping = pick(mapping, columns);
      })
    );
  };

  const setX = (event: React.ChangeEvent<HTMLSelectElement>) => {
    return produce(mapping, (draft) => {
      x && delete draft[x];
      draft[event.target.value] = "x";
    });
  };

  const setY = (prev: string, event: React.ChangeEvent<HTMLSelectElement>) => {
    return produce(mapping, (draft) => {
      prev && delete draft[prev];
      draft[event.target.value] = "y";
    });
  };

  const setZ = (event: React.ChangeEvent<HTMLSelectElement>) => {
    return produce(mapping, (draft) => {
      z && delete draft[z];
      event.target.value && (draft[event.target.value] = "z");
    });
  };

  const setGroup = (event: React.ChangeEvent<HTMLSelectElement>) => {
    return produce(mapping, (draft) => {
      group && delete draft[group];
      event.target.value && (draft[event.target.value] = "series");
    });
  };

  const addY = (event: React.ChangeEvent<HTMLSelectElement>) => {
    return produce(mapping, (draft) => {
      draft[event.target.value] = "y";
    });
  };

  return (
    <OptionsBox title="Result data">
      <OptionsFields size="xs">
        <OptionsFieldData
          label="X column"
          values={values}
          value={x ?? ""}
          onChange={(event) => replaceMapping(setX(event))}
        />
        {props.columns.ys.map((c, i) => (
          <OptionsFieldData
            key={c.name}
            label={`Y column ${i + 1}`}
            values={values}
            value={c.name ?? ""}
            onChange={(event) => replaceMapping(setY(c.name, event))}
          />
        ))}
        <OptionsFieldData
          label={`Y column ${props.columns.ys.length + 1}`}
          values={values}
          value=""
          onChange={(event) => replaceMapping(addY(event))}
        />
        <OptionsFieldData
          label="Group by"
          values={values}
          value={group ?? ""}
          onChange={(event) => replaceMapping(setGroup(event))}
        />
        {props.visual.options.globalSeriesType === "scatter" && (
          <OptionsFieldData
            label="Circle radius"
            values={numberValues}
            value={z ?? ""}
            onChange={(event) => replaceMapping(setZ(event))}
          />
        )}
      </OptionsFields>
    </OptionsBox>
  );
};

const OptionsChartSeries: React.FC<{
  visual: VisualChart;
  result: QueryResult;
  columns: ChartColumns;
}> = (props) => {
  const { pairedOptions, options } = createSortedChartSeriesOptions(
    props.visual,
    props.result,
    props.columns
  );

  const yAxis1 = props.visual.options.yAxis?.[1];

  if (pairedOptions.length === 0) {
    return null;
  }

  if (pairedOptions.length > 1000) {
    return <OptionsError>Too many chart series (max 1k).</OptionsError>;
  }

  return (
    <OptionsBox className={styles.series}>
      <table>
        <thead>
          <tr>
            <th>Series</th>
            <th>Chart type</th>
            {yAxis1 && <th>Y-axis</th>}
            <th>Color</th>
            <th />
          </tr>
        </thead>
        <tbody>
          {pairedOptions.map(([name], i) => (
            <OptionsChartSeriesRow
              visual={props.visual}
              key={name}
              name={name}
              rows={options}
              prev={pairedOptions[i - 1]?.[0]}
              next={pairedOptions[i + 1]?.[0]}
            />
          ))}
        </tbody>
      </table>
    </OptionsBox>
  );
};

const OptionsChartSeriesRow: React.FC<{
  visual: VisualChart;
  name: string | number;
  rows: SeriesOptions;
  prev?: string | number;
  next?: string | number;
}> = (props) => {
  const saveAndDispatch = useVisualSaveAndDispatch();
  const yAxis1 = props.visual.options.yAxis?.[1];

  const prev = props.rows[props.prev ?? ""];
  const next = props.rows[props.next ?? ""];
  const row = props.rows[props.name];

  const replaceOptions = (series: SeriesOptions) => {
    saveAndDispatch(
      produce(props.visual, (draft) => {
        draft.options.seriesOptions = series;
      })
    );
  };

  const moveUp = () => {
    return produce(props.rows, (draft) => {
      if (props.prev !== undefined && prev !== undefined) {
        draft[props.prev]!.zIndex = row.zIndex;
        draft[props.name]!.zIndex = prev.zIndex;
      }
    });
  };

  const moveDown = () => {
    return produce(props.rows, (draft) => {
      if (props.next !== undefined && next !== undefined) {
        draft[props.next]!.zIndex = row.zIndex;
        draft[props.name]!.zIndex = next.zIndex;
      }
    });
  };

  const setRow = (row: SeriesOptionsRow) => {
    return produce(props.rows, (draft) => {
      draft[props.name] = row;
    });
  };

  const setName = (event: React.ChangeEvent<HTMLInputElement>) => {
    return produce(row, (draft) => {
      draft.name = event.target.value;
    });
  };

  const setType = (event: React.ChangeEvent<HTMLSelectElement>) => {
    return produce(row, (draft) => {
      draft.type = event.target.value as SeriesOptionsRow["type"];
    });
  };

  const setYAxis = (event: React.ChangeEvent<HTMLSelectElement>) => {
    return produce(row, (draft) => {
      draft.yAxis = parseInt(event.target.value, 10);
    });
  };

  const setColor = (color?: string) => {
    return produce(row, (draft) => {
      draft.color = color;
    });
  };

  return (
    <tr>
      <td>
        <InputText
          placeholder="0x"
          aria-label="Series name"
          icon="text"
          value={row.name ?? props.name}
          onChange={(event) => replaceOptions(setRow(setName(event)))}
          size="sm"
          border="none"
        />
      </td>
      <td>
        <InputSelect
          aria-label="Chart type"
          icon={visualSeriesIcon(row.type)}
          value={row.type}
          onChange={(event) => replaceOptions(setRow(setType(event)))}
          disabled={props.visual.options.globalSeriesType === "pie"}
          size="sm"
          border="none"
        >
          <option value="column">Column</option>
          <option value="area">Area</option>
          <option value="scatter">Scatter</option>
          <option value="line">Line</option>
          <option value="pie">Pie</option>
        </InputSelect>
      </td>
      {yAxis1 && (
        <td>
          <InputSelect
            aria-label="Y-axis"
            value={row.yAxis}
            onChange={(event) => replaceOptions(setRow(setYAxis(event)))}
            disabled={props.visual.options.globalSeriesType === "pie"}
            size="sm"
            border="none"
          >
            <option value="0">Left</option>
            <option value="1">Right</option>
          </InputSelect>
        </td>
      )}
      <td>
        <OptionInputColor
          visual={props.visual}
          value={row.color}
          onChange={(color) => replaceOptions(setRow(setColor(color)))}
          disabled={props.visual.options.globalSeriesType === "pie"}
        />
      </td>
      <td>
        <div className={styles.order}>
          <button
            type="button"
            disabled={props.prev === undefined}
            onClick={() => replaceOptions(moveUp())}
          >
            <Icon icon="arrow-up" aria-label="Move up" />
          </button>
          <button
            type="button"
            disabled={props.next === undefined}
            onClick={() => replaceOptions(moveDown())}
          >
            <Icon icon="arrow-down" aria-label="Move down" />
          </button>
        </div>
      </td>
    </tr>
  );
};

const OptionsChartValues: React.FC<{
  visual: VisualChart;
  result: QueryResult;
  columns: ChartColumns;
}> = (props) => {
  const rows = props.result.matrix?.rows ?? [];
  const x = props.columns.x?.name;

  if (!x || rows.length === 0) {
    return null;
  }

  if (rows.length > 1000) {
    return <OptionsError>Too many chart values (max 1k).</OptionsError>;
  }

  return (
    <OptionsBox className={styles.series}>
      <table>
        <thead>
          <tr>
            <th className={styles.values}>Values</th>
            <th>Color</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((_row, i) => (
            <OptionsChartValuesRow
              {...props}
              key={i}
              column={String(props.result.matrix?.rows[i][x])}
            />
          ))}
        </tbody>
      </table>
    </OptionsBox>
  );
};

const OptionsChartValuesRow: React.FC<{
  visual: VisualChart;
  result: QueryResult;
  columns: ChartColumns;
  column: string;
}> = (props) => {
  const saveAndDispatch = useVisualSaveAndDispatch();
  const row = props.visual.options.valuesOptions?.[props.column] ?? {};

  const replaceOptions = (value: ValuesOptionsRow) => {
    saveAndDispatch(
      produce(props.visual, (draft) => {
        draft.options.valuesOptions = draft.options.valuesOptions ?? {};
        draft.options.valuesOptions[props.column] = value;
      })
    );
  };

  const setColor = (color?: string) => {
    return produce(row, (draft) => {
      draft.color = color;
    });
  };

  if (!row) {
    return null;
  }

  return (
    <tr>
      <td>
        <InputText
          placeholder="Dune"
          aria-label="Value title"
          icon="text"
          value={props.column}
          disabled
          size="sm"
          border="none"
        />
      </td>
      <td>
        <OptionInputColor
          visual={props.visual}
          value={row.color}
          onChange={(color) => replaceOptions(setColor(color))}
        />
      </td>
    </tr>
  );
};

const OptionInputColor: React.FC<{
  visual: VisualChart;
  value?: string;
  onChange: (value?: string) => void;
  disabled?: boolean;
}> = (props) => {
  const targetRef = React.useRef<HTMLButtonElement | null>(null);
  const popoverRef = React.useRef<HTMLDivElement | null>(null);
  const [openPopover, setOpenPopover] = React.useState(false);

  // Call onChange with no value to reset the color selection.
  const onReset = (event: React.SyntheticEvent) => {
    event.stopPropagation();
    props.onChange();
  };

  const handleClick = () => {
    if (!props.disabled) {
      setOpenPopover(true);
    }
  };

  React.useEffect(() => {
    function handleClickOutside(event: MouseEvent): void {
      if (
        popoverRef.current &&
        !popoverRef.current.contains(event.target as Node)
      ) {
        setOpenPopover(false);
      }
    }
    // Bind the event listener
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      // Unbind the event listener on clean up
      document.removeEventListener("mousedown", handleClickOutside);
    };
  });

  return (
    <div className={styles.color}>
      <div className={styles.popover}>
        <button
          ref={targetRef}
          onClick={handleClick}
          className={styles.colorTile}
          style={
            props.disabled
              ? { backgroundColor: "#FFFFFF", cursor: "not-allowed" }
              : { backgroundColor: props.value, cursor: "pointer" }
          }
        />
        <Popover
          hidden={!openPopover}
          targetRef={targetRef}
          position={positionDefault}
        >
          <div ref={popoverRef} className={styles.picker}>
            <HexColorPicker
              color={props.value || "FFFFFF"}
              onChange={debounce(props.onChange, 100)}
            />
            <HexColorInput
              color={props.value || "FFFFFF"}
              prefixed={true}
              onChange={debounce(props.onChange, 100)}
              className={styles.colorInput}
            />
          </div>
        </Popover>
      </div>

      <button type="button" disabled={!props.value} onClick={onReset}>
        <Icon icon="x" aria-label="Reset" />
      </button>
    </div>
  );
};

const OptionsFieldData: React.FC<{
  label: string;
  values: string[];
  value: string;
  onChange: (event: React.ChangeEvent<HTMLSelectElement>) => void;
}> = (props) => {
  return (
    <FieldLabel label={props.label} size="xs">
      <InputSelect
        value={props.value}
        onChange={props.onChange}
        size="sm"
        border="none"
      >
        {props.values.map((v) => (
          <option key={v} value={v}>
            {v}
          </option>
        ))}
      </InputSelect>
    </FieldLabel>
  );
};

const VisualUpdatingIndicator: React.FC<{
  visualUpdatingState: VisualUpdatingState;
}> = (props) => {
  const error = props.visualUpdatingState.error;
  const isLoading = props.visualUpdatingState.isLoading;
  if (error) {
    return (
      <p className={styles.error}>
        {`Failed to update options: ${props.visualUpdatingState.error}`}
      </p>
    );
  } else if (isLoading) {
    return (
      <div className={styles.loadingIcon}>
        <Icon icon="running" />
      </div>
    );
  }
  return null;
};

const OptionsHeader: React.FC = () => {
  const visual = useEditorVisual();
  const temp = useEditorQueryTemp();
  const visualUpdatingState = useEditorLastVisualUpdatingState();
  const unsaved = !visual?.id || temp;

  if (!visual) {
    return null;
  }

  return (
    <header className={styles.header}>
      <Grid className={styles.grid}>
        <ButtonGroup className={styles.leftItem}>
          <WidgetsEntriesButton item={visual} disabled={unsaved} />
        </ButtonGroup>
        {visualUpdatingState && (
          <VisualUpdatingIndicator visualUpdatingState={visualUpdatingState} />
        )}
      </Grid>
    </header>
  );
};

const Options: React.FC<{
  className: string;
  children: React.ReactNode;
}> = (props) => {
  return (
    <div className={cn(styles.options, props.className)}>{props.children}</div>
  );
};

const OptionsBox: React.FC<{
  title?: string;
  className?: string;
  children: React.ReactNode;
}> = (props) => {
  return (
    <Box className={props.className} title={props.title} size="sm" color1>
      {props.children}
    </Box>
  );
};

const OptionsError: React.FC<{ children: React.ReactNode }> = (props) => {
  return (
    <Box className={styles.error} title="Error" size="sm" color1>
      {props.children}
    </Box>
  );
};

const OptionsFields: React.FC<{
  horizontal?: boolean;
  size: Extract<Size, "xs">;
  children: React.ReactNode;
}> = (props) => {
  return (
    <Fields horizontal={props.horizontal} size={props.size}>
      {props.children}
    </Fields>
  );
};

// List chart columns based on the result data and column mappings.
export const createChartColumns = (
  visual: VisualChart,
  result: QueryResult
): ChartColumns => {
  const map = visual.options.columnMapping ?? {};
  const columns = result.matrix?.columns ?? [];

  const xs = columns.filter((c) => map[c.name] === "x");
  const ys = columns.filter((c) => map[c.name] === "y");
  const zs = columns.filter((c) => map[c.name] === "z");
  const gs = columns.filter((c) => map[c.name] === "series");

  return {
    columns,
    x: xs[0],
    z: zs[0],
    ys,
    group: gs[0],
  };
};

interface CreateSortedChartSeriesOptions {
  pairedOptions: Array<
    [
      string | number,
      SeriesOptionsRow & {
        zIndex: number;
      },
    ]
  >;
  options: SeriesOptions;
}

// Generate series options object for all series in the chart.
export const createSortedChartSeriesOptions = (
  visual: VisualChart,
  result: QueryResult,
  columns: ChartColumns
): CreateSortedChartSeriesOptions => {
  const rows = result.matrix?.rows ?? [];
  const group = columns.group?.name ?? "";

  const names = columns.group
    ? [...new Set(rows.map((r) => resultPrimitive(r[group])))]
    : columns.ys.map((y) => y.name);

  // Sort by zIndex if the series has series options set.
  // Otherwise, put in the end
  const sorted = sortBy(names, (name) =>
    visual.options.seriesOptions?.[name ?? ""]
      ? visual.options.seriesOptions?.[name ?? ""].zIndex
      : names.length
  );

  const pairedOptions: Array<[string | number, SeriesOptionsRow]> = sorted.map(
    (name, i) => [
      name ?? "",
      visual.options.seriesOptions?.[name ?? ""]
        ? {
            ...visual.options.seriesOptions?.[name ?? ""],
            zIndex: i,
          }
        : {
            type: visual.options.globalSeriesType,
            zIndex: i,
            yAxis: 0,
          },
    ]
  );

  return {
    pairedOptions,
    // Objects don't guarantee object property order
    options: fromPairs(pairedOptions),
  };
};
